'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

interface ScrollToHashProps {
  offset?: number
}

export function ScrollToHash({ offset = 120 }: ScrollToHashProps) {
  const pathname = usePathname()

  useEffect(() => {
    const hash = window.location.hash

    // If there's a hash, scroll to it with offset (cross-page navigation)
    if (hash) {
      const elementId = hash.slice(1)

      // Wait for the DOM to be ready and painted
      const scrollToElement = () => {
        const element = document.getElementById(elementId)

        if (!element) {
          // Element not found, try again after a short delay
          setTimeout(scrollToElement, 100)
          return
        }

        // Calculate position with offset
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - offset

        // Use 'smooth' for cross-page navigation with hash
        window.scrollTo({
          top: offsetPosition,
          behavior: 'auto'
        })
      }

      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        setTimeout(scrollToElement, 0)
      })
    }

    // Handle hash changes within the same page (in-page navigation)
    const handleHashChange = () => {
      const newHash = window.location.hash
      if (!newHash) return

      const newElementId = newHash.slice(1)
      const newElement = document.getElementById(newElementId)

      if (!newElement) return

      const elementPosition = newElement.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      // Use 'auto' for instant scroll on in-page hash changes
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [pathname, offset])

  return null
}
